import React from 'react';
import styled, { css } from 'styled-components';

import type { ITable, ITableHeaderCell, ITableRow, TableType } from '@/components/common/types';

import { media } from '@/styles/theme';
import { ArrowDropUp } from '@/assets/icons';
import { classList, lineClamp } from '@/helpers';
import { otherColors } from '@/styles/colors';

const Table = ({ children, minWidth, className, type, tableRef, overflowContainerRef }: ITable): React.ReactElement => {
  return (
    <OverflowContainer type={type} ref={overflowContainerRef}>
      <Container minWidth={minWidth} className={classList(className, type)} ref={tableRef}>
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              type,
            });
          }
          return child;
        })}
      </Container>
    </OverflowContainer>
  );
};

const TableRow = ({
  children,
  firstBgColor,
  secondBgColor,
  className,
  onClick,
  height,
  alignItem,
  padding,
  type,
}: ITableRow): React.ReactElement => (
  <Row
    height={height}
    firstBgColor={firstBgColor}
    secondBgColor={secondBgColor}
    className={className}
    onClick={onClick}
    alignItem={alignItem}
    padding={padding}
    $type={type}
  >
    {children}
  </Row>
);

const OverflowContainer = styled.div<{ type?: TableType }>`
  ${({ type }) =>
    type === 'floating' &&
    css`
      position: relative;
      overflow: auto;
      border-left: 1px solid #e5e7eb;
      border-right: 1px solid #e5e7eb;
      margin-top: 10px;
      margin-bottom: 10px;
      border-radius: 8px;

      & > div {
        margin-top: 0;
        margin-bottom: 0;
        border-left-width: 0;
        border-right-width: 0;
      }

      .table {
        min-width: initial;
        width: 100%;
      }

      ${media.laptop} {
        overflow: initial;
      }
    `}
`;

const Container = styled.div<{ minWidth?: number }>`
  margin-top: 8px;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
  border-radius: 8px;

  &.floating,
  &.scrolling {
    overflow: auto;
  }

  & > * {
    ${media.tablet} {
      ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}px;` : '')}
    }
  }
`;

const Header = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: start;

  height: 50px;
  padding: 14px 16px 14px 31px;

  &.small {
    height: 36px;
  }

  background-color: #fff;
  border-radius: 8px 8px 0 0;
`;

const HeaderCell = ({ children, className, onClick }: ITableHeaderCell): React.ReactElement => (
  <div className={classList(className, 'table-header-cell-root')} onClick={onClick}>
    <div className="sorting-state">
      <div className="sorting-state-asc" />
      <div className="sorting-state-desc" />
    </div>
    {children}
  </div>
);

const HeaderCellStyled = styled(HeaderCell)`
  ${({ width }) => (width ? `width: ${width}` : '')};
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}` : '')};
  ${({ flex }) => (flex ? `flex: ${flex}` : '')};
  margin-right: 8px;

  font-weight: 700;
  font-size: 12px;
  line-height: 20px;
  color: #4b5563;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &.hide-on-small-screen {
    display: none;

    ${media.tablet} {
      display: block;
    }
  }

  .table-header-cell-root {
    display: flex;
    position: relative;
  }

  .sorting-state {
    display: none;
    position: absolute;
    left: 0;
  }

  ${({ sortingEnabled, sortingState }) =>
    sortingEnabled &&
    css`
      cursor: pointer;

      .sorting-state {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 10px;
        height: 20px;
        margin-left: 4px;
        padding: 5px 0;
        gap: 2px;

        .sorting-state-asc {
          display: ${sortingState === 'DESC' ? 'none' : 'block'};
          mask-image: url(${ArrowDropUp});
          background-color: ${sortingState === 'ASC' ? otherColors.lxc.blue : '#ccc'};
          width: 10px;
          height: 5px;
        }

        .sorting-state-desc {
          display: ${sortingState === 'ASC' ? 'none' : 'block'};
          mask-image: url(${ArrowDropUp});
          background-color: ${sortingState === 'DESC' ? otherColors.lxc.blue : '#ccc'};
          transform: rotate3d(0, 0, 1, 180deg);
          width: 10px;
          height: 5px;
        }
      }
    `};
`;

const Row = styled.div<{
  firstBgColor?: string;
  secondBgColor?: string;
  height?: string;
  alignItem?: string;
  padding?: string;
  $type?: TableType;
}>`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: ${props => props.alignItem || 'center'};
  justify-content: start;
  height: ${props => props.height ?? '64px'};
  padding: 14px 16px 14px 31px;
  ${({ padding }) => padding && `padding: ${padding}`};

  &.small {
    height: 48px;
  }

  background-color: ${({ secondBgColor }) => secondBgColor || '#fff'};

  &:nth-child(2n) {
    background-color: ${({ firstBgColor }) => firstBgColor || '#f3f4f6'};
    ${({ $type, firstBgColor }) =>
      $type === 'floating' &&
      css`
        min-width: fit-content;

        & > :last-child {
          background-image: linear-gradient(to right, transparent 0%, ${firstBgColor} 25%, ${firstBgColor} 100%);
        }
      `};
  }

  ${({ $type, secondBgColor }) =>
    $type === 'floating' &&
    css`
      position: static;
      padding-right: 50px;

      & > :last-child {
        position: absolute;
        height: 100%;
        background-image: linear-gradient(to right, transparent 0%, ${secondBgColor} 25%, ${secondBgColor} 100%);
        display: flex;
        align-items: center;
        right: 0;
        overflow: visible;
        width: 60px;
        margin-right: 0;
        padding-left: 15px;
        border-radius: 4px;
      }
    `};
`;

const Cell = styled.div<{
  width?: string;
  minWidth?: string;
  maxHeight?: string;
  multiline?: number;
  flex?: string;
  alignSelf?: string;
}>`
  ${({ width }) => (width ? `width: ${width}` : '')};
  ${({ minWidth }) => (minWidth ? `min-width: ${minWidth}` : '')};
  ${({ flex }) => (flex ? `flex: ${flex}` : '')};
  max-height: ${props => props.maxHeight ?? '43px'};
  margin-right: 8px;

  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #374151;
  text-overflow: ellipsis;
  overflow: hidden;
  align-self: ${props => (props.alignSelf ? props.alignSelf : 'center')};

  ${({ multiline }) =>
    !multiline || multiline < 1
      ? css`
          white-space: nowrap;
        `
      : css`
          white-space: normal;
          overflow-wrap: break-word;
          ${lineClamp(multiline)}
        `}
  &.hide-on-small-screen {
    display: none;

    ${media.tablet} {
      display: block;
    }
  }

  &.dropdown-action {
    overflow: visible;
  }

  &.sessions-table-cell {
    max-height: 48px;
  }
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  line-height: 20px;
  color: #4b5563;
`;

Table.Header = Header;
Table.HeaderCell = HeaderCellStyled;
Table.TableRow = TableRow;
Table.Row = Row;
Table.Cell = Cell;
Table.Title = Title;

export { Table };
